import React, { useEffect, useState } from "react";

const CustomTableWrapper = ({
  data = [],
  itemsPerPage = 10,
  columns = [],
  renderRow,
  enableSearch = false,
  searchKeys = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(itemsPerPage);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Sanitize data to avoid non-array issues
  const sanitizedData = Array.isArray(data) ? data : [];

//   if (sanitizedData.length === 0) {
//   console.warn("⚠️ No data passed to table. Using test data temporarily.");
//   sanitizedData.push({
//     invoice_no: "#123",
//     invoice_Type: "Test",
//     companyName: "Kapil Inc",
//     invoice_date: "2025-08-01",
//     invoice_due_date: "2025-08-10",
//     Total_amount: 1000,
//     status: "Paid"
//   });
// }

  const handleSort = (field) => {
    if (!columns.find((col) => col.field === field)?.sortable) return;
    if (field === sortField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (field) => {
    const isActive = field === sortField;
    return (
      <span style={{ fontSize: "20px", marginLeft: "5px" }}>
        <span style={{ color: isActive && sortOrder === "asc" ? "#000" : "#ccc" }}>↑</span>
        <span style={{ color: isActive && sortOrder === "desc" ? "#000" : "#ccc", marginLeft: "2px" }}>↓</span>
      </span>
    );
  };

  const getSortedData = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    if (!sortField) return dataArray;
    return [...dataArray].sort((a, b) => {
      const valA = (a[sortField] || "").toString().toLowerCase();
      const valB = (b[sortField] || "").toString().toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getFilteredData = () => {
    if (!enableSearch || !searchTerm.trim()) return sanitizedData;
    const term = searchTerm.toLowerCase();
    return sanitizedData.filter((item) =>
      searchKeys.some((key) =>
        (item[key] || "").toString().toLowerCase().includes(term)
      )
    );
  };

  const filteredData = getFilteredData();
  const sortedData = getSortedData(filteredData);
  const totalPages = Math.ceil(sortedData.length / currentPageSize);
  const currentData = sortedData.slice(
    (currentPage - 1) * currentPageSize,
    currentPage * currentPageSize
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredData.length, currentPageSize]);

  return (
    <div className="table-wrapper bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3 p-3">
        <div className="d-flex align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={currentPageSize}
            onChange={(e) => {
              setCurrentPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>

        {enableSearch && (
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control ps-5 py-2 shadow-sm rounded-pill"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i
              className="bx bx-search position-absolute top-50 start-0 translate-middle-y ps-3 text-secondary"
              style={{ pointerEvents: "none" }}
            ></i>
          </div>
        )}
      </div>

      <table className="table table-responsive table-bordered mb-3">
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th
                key={idx}
                onClick={() => handleSort(col.field)}
                style={{ cursor: col.sortable ? "pointer" : "default" }}
              >
                {col.label}
                {col.sortable && renderSortIcon(col.field)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentData && currentData.length > 0 ? (
            currentData.map((item, index) =>
              renderRow(item, index, currentPage, currentPageSize)
            )
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing{" "}
          {sortedData.length === 0
            ? 0
            : (currentPage - 1) * currentPageSize + 1}{" "}
          to {Math.min(currentPage * currentPageSize, sortedData.length)} of{" "}
          {sortedData.length} entries
        </div>

        <div className="btn-group">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button className="btn btn-sm btn-dark" disabled>
            {currentPage}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomTableWrapper;
